import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import styles from "../styles/pages/privacypolicy.module.css";

const PrivacyPolicy = () => {
    const canonicalUrl =
        "https://myonlineshopping.co.in/privacy-policy";

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Privacy Policy - Mohit's Shopping App",
        description:
            "Privacy Policy for Mohit's Shopping App, an Amazon affiliate shopping website.",
        url: canonicalUrl,
        isPartOf: {
            "@type": "WebSite",
            name: "Mohit's Shopping App",
            url: "https://myonlineshopping.co.in/",
        },
    };

    return (
        <>
            <Helmet>
                <title>Privacy Policy | Mohit's Shopping App</title>

                <meta
                    name="description"
                    content="Read the Privacy Policy of Mohit's Shopping App to understand how information is handled when you use our Amazon affiliate shopping website."
                />

                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large"
                />

                <meta name="author" content="Mohit Singh" />

                <link
                    rel="canonical"
                    href={canonicalUrl}
                />

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    property="og:title"
                    content="Privacy Policy | Mohit's Shopping App"
                />

                <meta
                    property="og:description"
                    content="Learn how Mohit's Shopping App handles information and privacy when you browse our Amazon affiliate shopping website."
                />

                <meta
                    property="og:url"
                    content={canonicalUrl}
                />

                <meta
                    property="og:image"
                    content="https://myonlineshopping.co.in/favicon.png"
                />

                <meta
                    property="og:image:alt"
                    content="Mohit's Shopping App"
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="Privacy Policy | Mohit's Shopping App"
                />

                <meta
                    name="twitter:description"
                    content="Privacy information for Mohit's Shopping App."
                />

                <meta
                    name="twitter:image"
                    content="https://myonlineshopping.co.in/favicon.png"
                />

                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>

            <main className={styles.privacyPage}>
                {/* HERO */}
                <section className={styles.hero}>
                    <div className={styles.heroGlowOne}></div>
                    <div className={styles.heroGlowTwo}></div>

                    <Container>
                        <div className={styles.breadcrumb}>
                            <span>Home</span>
                            <i>/</i>
                            <span>Legal</span>
                            <i>/</i>
                            <strong>Privacy Policy</strong>
                        </div>

                        <div className={styles.heroContent}>
                            <div className={styles.heroText}>
                                <div className={styles.eyebrow}>
                                    <span className={styles.eyebrowIcon}>
                                        🔐
                                    </span>
                                    Privacy & Security
                                </div>

                                <h1>
                                    Privacy
                                    <span> Policy</span>
                                </h1>

                                <p>
                                    Your privacy matters to us. Learn how
                                    information is handled while using
                                    Mohit's Shopping App.
                                </p>

                                <div className={styles.heroMeta}>
                                    <div className={styles.metaItem}>
                                        <span className={styles.metaIcon}>
                                            ↻
                                        </span>

                                        <div>
                                            <small>Last updated</small>
                                            <strong>
                                                August 26, 2026
                                            </strong>
                                        </div>
                                    </div>

                                    <div className={styles.metaDivider}></div>

                                    <div className={styles.metaItem}>
                                        <span className={styles.metaIcon}>
                                            🔒
                                        </span>

                                        <div>
                                            <small>Privacy</small>
                                            <strong>
                                                Your information matters
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.heroVisual}>
                                <div className={styles.documentCard}>
                                    <div className={styles.documentTop}>
                                        <div className={styles.docLogo}>
                                            MS
                                        </div>

                                        <div className={styles.docLines}>
                                            <span></span>
                                            <span></span>
                                        </div>

                                        <div className={styles.checkCircle}>
                                            ✓
                                        </div>
                                    </div>

                                    <div className={styles.lockVisual}>
                                        <div className={styles.lockShackle}></div>

                                        <div className={styles.lockBody}>
                                            <span>✓</span>
                                        </div>
                                    </div>

                                    <div className={styles.docTitle}>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>

                                    <div className={styles.docContent}>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>

                                    <div className={styles.docFooter}>
                                        <span>PRIVACY</span>
                                        <span>2026</span>
                                    </div>
                                </div>

                                <div className={styles.floatingBadge}>
                                    <span>✓</span>
                                    Privacy Protected
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* CONTENT */}
                <Container className={styles.contentContainer}>
                    <div className={styles.layout}>
                        {/* SIDEBAR */}
                        <aside className={styles.sidebar}>
                            <div className={styles.sidebarInner}>
                                <div className={styles.sidebarTitle}>
                                    <span>Contents</span>
                                    <small>11 sections</small>
                                </div>

                                <nav className={styles.toc}>
                                    <a href="#introduction">
                                        <span>01</span>
                                        <strong>Introduction</strong>
                                    </a>

                                    <a href="#amazon-model">
                                        <span>02</span>
                                        <strong>
                                            Our Amazon Affiliate Model
                                        </strong>
                                    </a>

                                    <a href="#information">
                                        <span>03</span>
                                        <strong>
                                            Information You Provide
                                        </strong>
                                    </a>

                                    <a href="#automatic">
                                        <span>04</span>
                                        <strong>
                                            Automatically Collected
                                            Information
                                        </strong>
                                    </a>

                                    <a href="#cookies">
                                        <span>05</span>
                                        <strong>
                                            Cookies and Similar Technologies
                                        </strong>
                                    </a>

                                    <a href="#third-party">
                                        <span>06</span>
                                        <strong>
                                            Third-Party Websites
                                        </strong>
                                    </a>

                                    <a href="#amazon-data">
                                        <span>07</span>
                                        <strong>
                                            Amazon Purchases and Personal Data
                                        </strong>
                                    </a>

                                    <a href="#security">
                                        <span>08</span>
                                        <strong>Data Security</strong>
                                    </a>

                                    <a href="#children">
                                        <span>09</span>
                                        <strong>Children's Privacy</strong>
                                    </a>

                                    <a href="#changes">
                                        <span>10</span>
                                        <strong>
                                            Changes to This Privacy Policy
                                        </strong>
                                    </a>

                                    <a href="#contact">
                                        <span>11</span>
                                        <strong>Contact Us</strong>
                                    </a>
                                </nav>

                                <div className={styles.helpCard}>
                                    <div className={styles.helpIcon}>
                                        ?
                                    </div>

                                    <strong>Need help?</strong>

                                    <p>
                                        Have a question about our privacy
                                        policy? We're happy to help.
                                    </p>

                                    <a href="https://myonlineshopping.co.in/contact-us">
                                        Contact us
                                        <span>→</span>
                                    </a>
                                </div>
                            </div>
                        </aside>

                        {/* MAIN ARTICLE */}
                        <article className={styles.article}>
                            {/* NOTICE */}
                            <div className={styles.notice}>
                                <div className={styles.noticeIcon}>
                                    🔐
                                </div>

                                <div className={styles.noticeContent}>
                                    <span className={styles.noticeLabel}>
                                        Important
                                    </span>

                                    <h2>
                                        Our Amazon affiliate model
                                    </h2>

                                    <p>
                                        Mohit's Shopping App is an Amazon
                                        affiliate website. We provide product
                                        information and affiliate links that
                                        may redirect you to Amazon. We do not
                                        directly sell or process purchases of
                                        Amazon products.
                                    </p>
                                </div>

                                <div className={styles.noticeArrow}>
                                    ↗
                                </div>
                            </div>

                            {/* 1 */}
                            <section
                                id="introduction"
                                className={styles.privacySection}
                            >
                                <div className={styles.sectionNumber}>
                                    01
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>1. Introduction</h2>

                                    <p>
                                        Welcome to Mohit's Shopping App
                                        ("Website", "we", "us", or "our").
                                    </p>

                                    <p>
                                        This Privacy Policy explains how
                                        information may be collected, used,
                                        and protected when you visit and use
                                        our website.
                                    </p>

                                    <p>
                                        By using our website, you acknowledge
                                        that you have read and understood this
                                        Privacy Policy.
                                    </p>
                                </div>
                            </section>

                            {/* 2 */}
                            <section
                                id="amazon-model"
                                className={styles.privacySection}
                            >
                                <div className={styles.sectionNumber}>
                                    02
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>2. Our Amazon Affiliate Model</h2>

                                    <p>
                                        Mohit's Shopping App operates as an
                                        affiliate and product discovery
                                        website.
                                    </p>

                                    <p>
                                        We may display products available on
                                        Amazon and provide links that direct
                                        visitors to Amazon.
                                    </p>

                                    <p>
                                        When you click an affiliate link and
                                        purchase an eligible product on
                                        Amazon, we may receive a qualifying
                                        commission.
                                    </p>

                                    <p>
                                        We do not process your Amazon
                                        checkout, payment, shipping, return,
                                        refund, or cancellation.
                                    </p>
                                </div>
                            </section>

                            {/* 3 */}
                            <section
                                id="information"
                                className={styles.privacySection}
                            >
                                <div className={styles.sectionNumber}>
                                    03
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>3. Information You Provide</h2>

                                    <p>
                                        Depending on the features available
                                        on our website, you may voluntarily
                                        provide information such as your name,
                                        email address, or other information
                                        when contacting us.
                                    </p>

                                    <p>
                                        Please do not submit sensitive payment
                                        information, Amazon account passwords,
                                        or other confidential credentials
                                        through our website.
                                    </p>
                                </div>
                            </section>

                            {/* 4 */}
                            <section
                                id="automatic"
                                className={styles.privacySection}
                            >
                                <div className={styles.sectionNumber}>
                                    04
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>
                                        4. Automatically Collected Information
                                    </h2>

                                    <p>
                                        Our website may automatically receive
                                        technical information such as browser
                                        type, device type, operating system,
                                        approximate usage information,
                                        referring pages, and website
                                        interaction data.
                                    </p>

                                    <p>
                                        This information may be used to
                                        improve website performance, security,
                                        usability, and overall user
                                        experience.
                                    </p>
                                </div>
                            </section>

                            {/* 5 */}
                            <section
                                id="cookies"
                                className={styles.privacySection}
                            >
                                <div className={styles.sectionNumber}>
                                    05
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>
                                        5. Cookies and Similar Technologies
                                    </h2>

                                    <p>
                                        Our website may use cookies or
                                        similar technologies for functionality,
                                        analytics, performance measurement,
                                        and affiliate referral tracking.
                                    </p>

                                    <p>
                                        Third-party websites, including
                                        Amazon, may use their own cookies and
                                        tracking technologies according to
                                        their respective policies.
                                    </p>
                                </div>
                            </section>

                            {/* 6 */}
                            <section
                                id="third-party"
                                className={styles.privacySection}
                            >
                                <div className={styles.sectionNumber}>
                                    06
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>6. Third-Party Websites</h2>

                                    <p>
                                        Our website may contain links to
                                        Amazon and other third-party
                                        websites.
                                    </p>

                                    <p>
                                        Once you leave our website, the
                                        privacy policy and terms of the
                                        third-party website apply.
                                    </p>

                                    <p>
                                        We do not control how third-party
                                        websites collect or process
                                        information.
                                    </p>
                                </div>
                            </section>

                            {/* 7 */}
                            <section
                                id="amazon-data"
                                className={styles.privacySection}
                            >
                                <div className={styles.sectionNumber}>
                                    07
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>
                                        7. Amazon Purchases and Personal Data
                                    </h2>

                                    <p>
                                        Purchases made after clicking an
                                        Amazon affiliate link are completed
                                        directly on Amazon.
                                    </p>

                                    <p>
                                        Your Amazon account information,
                                        payment information, order
                                        information, delivery information,
                                        and transaction details are handled
                                        by Amazon according to its applicable
                                        policies.
                                    </p>

                                    <p>
                                        We do not require your Amazon password
                                        or complete payment card information
                                        to use our website.
                                    </p>
                                </div>
                            </section>

                            {/* 8 */}
                            <section
                                id="security"
                                className={styles.privacySection}
                            >
                                <div className={styles.sectionNumber}>
                                    08
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>8. Data Security</h2>

                                    <p>
                                        We take reasonable measures to protect
                                        information handled directly through
                                        our website.
                                    </p>

                                    <p>
                                        However, no website, server, or
                                        internet transmission can be
                                        guaranteed to be completely secure.
                                    </p>
                                </div>
                            </section>

                            {/* 9 */}
                            <section
                                id="children"
                                className={styles.privacySection}
                            >
                                <div className={styles.sectionNumber}>
                                    09
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>9. Children's Privacy</h2>

                                    <p>
                                        Our website is not intentionally
                                        designed to collect personal
                                        information from children.
                                    </p>

                                    <p>
                                        If you believe that a child has
                                        submitted personal information through
                                        our website, please contact us.
                                    </p>
                                </div>
                            </section>

                            {/* 10 */}
                            <section
                                id="changes"
                                className={styles.privacySection}
                            >
                                <div className={styles.sectionNumber}>
                                    10
                                </div>

                                <div className={styles.sectionBody}>
                                    <h2>
                                        10. Changes to This Privacy Policy
                                    </h2>

                                    <p>
                                        We may update this Privacy Policy from
                                        time to time to reflect changes in our
                                        website, services, technologies, or
                                        applicable requirements.
                                    </p>

                                    <p>
                                        Any updated version will be published
                                        on this page with a revised "Last
                                        updated" date.
                                    </p>
                                </div>
                            </section>

                            {/* 11 */}
                            <section
                                id="contact"
                                className={styles.contactSection}
                            >
                                <div className={styles.contactTop}>
                                    <div>
                                        <span className={styles.contactLabel}>
                                            GET IN TOUCH
                                        </span>

                                        <h2>11. Contact Us</h2>

                                        <p>
                                            If you have questions about this
                                            Privacy Policy or information
                                            handled directly by our website,
                                            contact us:
                                        </p>
                                    </div>

                                    <div className={styles.contactSymbol}>
                                        ✦
                                    </div>
                                </div>

                                <div className={styles.contactActions}>
                                    <a
                                        href="mailto:mahi8191singh@gmail.com"
                                        className={styles.contactButton}
                                    >
                                        <span className={styles.buttonIcon}>
                                            ✉
                                        </span>

                                        <span>
                                            <small>Email us</small>
                                            <strong>
                                                mahi8191singh@gmail.com
                                            </strong>
                                        </span>

                                        <b>→</b>
                                    </a>

                                    <a
                                        href="tel:+918191042065"
                                        className={styles.contactButton}
                                    >
                                        <span className={styles.buttonIcon}>
                                            ☎
                                        </span>

                                        <span>
                                            <small>Call us</small>
                                            <strong>
                                                +91 8191042065
                                            </strong>
                                        </span>

                                        <b>→</b>
                                    </a>
                                </div>
                            </section>

                            <div className={styles.bottomNote}>
                                <span>✓</span>

                                By using our website, you acknowledge that you
                                have read and understood this Privacy Policy.
                            </div>
                        </article>
                    </div>
                </Container>
            </main>
        </>
    );
};

export default PrivacyPolicy;
